'use server';

import bcrypt from 'bcrypt';
import { revalidatePath } from 'next/cache';

import { clampWindowToAdminWindow } from '@/lib/access-window';
import { getEffectiveUserId } from '@/lib/actions/impersonation';
import { logAuditEvent } from '@/lib/audit-log';
import { getSession } from '@/lib/auth-session';
import { authorize, getCapabilitySummary, invalidateCapabilityCache, invalidateGrantCache } from '@/lib/authz/authorize';
import { type PendingRoleInput, validatePendingRoleAssignments } from '@/lib/authz/pending-role-assignments';
import { CacheInvalidation } from '@/lib/cache';
import { prisma } from '@/lib/db';
import { sendEmail } from '@/lib/email';
import { assertNever, pruefeEmailWechselOderSperren } from '@/lib/email-change-gate';
import {
    EMAIL_CHANGE_BLOCKED_MESSAGE,
    EMAIL_INVALID_MESSAGE,
    EMAIL_REQUIRED_MESSAGE,
    EMAIL_VERIFICATION_RATE_LIMITED_MESSAGE,
    EMAIL_VERIFICATION_SEND_FAILED_MESSAGE,
    isBlockedEmailChange,
    isPlausibleEmailAddress,
    normalizeEmail,
} from '@/lib/email-change-policy';
import { logger } from '@/lib/errors';
import { resolveMailBranding } from '@/lib/network-settings-branding';
import { type ActionResult, failure, ok } from '@/lib/types/action-result';
import { resolveSessionUserId } from '@/lib/auth';
import { revokeUserSessions } from '@/lib/auth-revocation';
import { updateUserIdentity } from '@/lib/auth-user-update';
import { hashToken } from '@/lib/token-hash';
import { normalizePhone } from '@/lib/phone';
import { isEmailReuseBlocked } from '@/lib/support-identity-gate';
import { detachZammadBinding } from '@/lib/zammad-binding';

import {
    buildDisplayName,
    buildUsernameBase,
    consumeVerificationEmailQuota,
    createEmailVerificationToken,
    findAvailableUsername,
    getActiveScopeGrantsForUser,
    getCurrentUserId,
    getMissingSmtpConfig,
    getRequestIp,
    isSmtpConfigured,
    MIN_PASSWORD_LENGTH,
    registerIpLimiter,
    resolveGrantorWindow,
    safeLogSystemError,
    SALT_ROUNDS,
    suggestUsernameIpLimiter,
    upsertCredentialAccount,
    verifyRecaptcha,
} from '../users-shared';

/**
 * Typed sentinel, mit dem wir die $transaction in `updateUserAdmin` abbrechen.
 * Ein Throw ist der einzige Weg, die Transaktion zurückzurollen; der äußere
 * catch verzweigt über `instanceof` (statt Message-Parsing) und liefert die
 * passende deutsche Fehlermeldung.
 */
class ConcurrentUpdateError extends Error {
    constructor() {
        super('CONCURRENT_UPDATE');
        this.name = 'ConcurrentUpdateError';
    }
}

export async function suggestUsername(
    firstName: string,
    lastName: string,
): Promise<ActionResult<{ suggestion: string }>> {
    // Code-review S-A (2026-05-13): the iteration counter exposed by
    // `findAvailableUsername` (`john.doe.4`) confirms how many users
    // share the canonical name → username-enumeration oracle. Bound
    // by IP so an unauthenticated attacker can't enumerate.
    const ip = await getRequestIp();
    const { success } = await suggestUsernameIpLimiter.limit(`suggest-username-ip:${ip}`);
    if (!success) {
        return ok({ suggestion: '' });
    }
    const base = buildUsernameBase(firstName, lastName);
    if (!base) return ok({ suggestion: '' });
    const suggestion = await findAvailableUsername(base);
    return ok({ suggestion });
}

/**
 * Portal-Admin-Anlage eines Benutzers.
 *
 * Review-Nachfassung 2026-07-25 (Problem 3): `emailVerified` ist KEIN
 * Eingabefeld mehr. Vorher galt `data.emailVerified ?? true`, der Admin-Dialog
 * schickte per Vorbelegung `true` — ein Konto auf eine FREMDE Adresse war
 * damit sofort „bestätigt" und sowohl anmeldbar als auch über
 * /api/zammad/sso-redirect als diese Person in Zammad wirksam. A1 war dadurch
 * nicht geschlossen, sondern nur von Netz-Admins auf Portal-Admins eingeengt.
 *
 * Warum nicht bloß der Default umgedreht: der Wert kam aus dem Client. Ein
 * umgedrehter Default hätte nichts geändert, solange der Dialog weiterhin
 * `true` sendet. Entscheidend ist die Bedeutung des Feldes — „bestätigt" ist
 * eine Aussage darüber, dass jemand das Postfach kontrolliert. Anders als
 * Passwort-Reset oder Impersonation (beide portalintern und auditiert)
 * verlässt `emailVerified` über den Zammad-SSO den Vertrauensbereich des
 * Portals: dort wird daraus eine Identität.
 *
 * Folge für den Arbeitsablauf (bewusst): Admin-angelegte Konten sind
 * unbestätigt und erhalten eine Bestätigungsmail; anmelden können sie sich
 * erst danach. Das ist exakt der Ablauf, den `createUserAsNetworkAdmin` seit
 * C2 fährt. Der Admin kann weiterhin ein Startpasswort setzen.
 *
 * Ausnahmefall Postfach nicht erreichbar (Test-/Dienst-/Übergabekonten):
 * dafür bleibt die AUSDRÜCKLICHE Freigabe im Bearbeiten-Dialog
 * (`updateUserAdmin` mit `emailVerified: true`, siehe dort). Sie ist eine
 * bewusste Einzelhandlung mit Audit-Eintrag statt einer Vorbelegung — und
 * gerade deshalb nicht mehr Teil der Anlage.
 */
export async function createUser(data: { firstName: string; lastName: string; email: string; username?: string; password?: string }): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Keine Berechtigung');

        const name = buildDisplayName(data.firstName, data.lastName);
        if (!name) return failure('Vorname und Nachname sind erforderlich');

        // Abschluss-Review 2026-07-29 (I2, zweiter Teil): Hier stand
        // `email: data.email` roh — ohne Trimmen, ohne Leer-, ohne
        // Formalprüfung. Damit war `email: ''` anlegbar, und `updateProfile`
        // begründet seine Gate-Pflicht für die Erstbefüllung mit dem Satz „Die
        // Wege, die `''` schrieben, sind seit Review 2026-07-27 zu". Der Satz
        // trägt eine Auflage — also wird er wahr gemacht statt umgeschrieben.
        //
        // `type="email"` im Anlage-Dialog ist keine Sicherung: Eine Server
        // Action ist ein öffentlicher Endpunkt. Dieselbe Prüfung fährt
        // `updateUserAdmin` seit Review 2026-07-27 und `updateProfile` in
        // beiden Adresszweigen.
        //
        // VERHALTENSÄNDERUNG AM ADMINPFAD, ausdrücklich festgehalten
        // (Nachprüfung 2026-07-29, N4): `isPlausibleEmailAddress` verlangt
        // eine Domain mit Punkt und buchstabiger Endung. Adressen, die bisher
        // anlegbar waren, sind es damit nicht mehr — `dienst@intranet`,
        // `admin@localhost`, jede reine Hostnamen-Adresse. Das ist gewollt und
        // seit dem 27.07. die Regel des Bearbeiten-Dialogs; neu ist nur, dass
        // sie jetzt auch für die ANLAGE gilt. Wer ein Dienst- oder Testkonto
        // ohne echtes Postfach braucht, legt es mit einer erreichbaren
        // Adresse an und nutzt die ausdrückliche Freigabe im
        // Bearbeiten-Dialog (`updateUserAdmin` mit `emailVerified: true`).
        const email = normalizeEmail(data.email);
        if (email === null || !isPlausibleEmailAddress(email)) {
            return failure(EMAIL_INVALID_MESSAGE);
        }

        const usernameInput = data.username?.trim() ?? '';
        const username = usernameInput || await findAvailableUsername(buildUsernameBase(data.firstName, data.lastName));
        if (!username) return failure('Benutzername ist erforderlich');

        const existingUsername = await prisma.user.findUnique({ where: { username } });
        if (existingUsername) return failure('Benutzername ist bereits vergeben');

        const hashedPassword = data.password ? await bcrypt.hash(data.password, SALT_ROUNDS) : undefined;

        // Immer unbestätigt — siehe Funktionskommentar (Problem 3 / A1).
        const newUser = await prisma.user.create({
            data: {
                name,
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                email,
                password: hashedPassword,
                username,
                emailVerified: null,
                creationSource: 'admin',
                createdById: effectiveUserId,
            }
        });

        // Create credential account entry for Better Auth
        if (hashedPassword) {
            await prisma.account.create({
                data: {
                    userId: newUser.id,
                    accountId: String(newUser.id),
                    providerId: 'credential',
                    provider: 'credential',
                    providerAccountId: String(newUser.id),
                    type: 'credential',
                    password: hashedPassword,
                },
            });
        }

        if (newUser.email) {
            const token = await createEmailVerificationToken(newUser.id);
            const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
            const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;
            // Portal-Admin-Anlage ohne Netz-Kontext: Stufe (b) über die (i. d. R.
            // noch leeren) Bindings des NEUEN Users → meist generisch (Spec #1).
            const branding = await resolveMailBranding({ userId: newUser.id });
            const emailResult = await sendEmail({
                to: newUser.email,
                subject: 'Bitte E-Mail-Adresse bestaetigen',
                text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
                html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href=\"${verificationUrl}\">${verificationUrl}</a></p>`,
                senderNamePostfix: branding.senderNamePostfix,
                signature: branding.signature,
            });
            if (emailResult.error) {
                return failure(emailResult.error);
            }
        }

        revalidatePath('/admin/users', 'layout');

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.create',
                resourceType: 'User',
                resourceId: newUser.id,
                newValue: { name: newUser.name, firstName: newUser.firstName, lastName: newUser.lastName, email: newUser.email, username: newUser.username },
            });
        }

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User create failed', e, { email: data.email, username: data.username });
        return failure('Fehler beim Erstellen des Benutzers');
    }
}

export async function createUserAsNetworkAdmin(data: {
    firstName: string;
    lastName: string;
    email: string;
    username?: string;
    password?: string;
    /**
     * Network the new user should be added to as `net_user`. Required when
     * the calling admin manages more than one network — without this the
     * UI must present a picker. Optional (auto-resolved) when the admin
     * manages exactly one network.
     */
    networkId?: number;
}): Promise<ActionResult<{ userId: number }>> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');

        const caps = await getCapabilitySummary(effectiveUserId);
        if (!caps.isNetworkAdmin && !caps.isAdmin) {
            return failure('Keine Berechtigung');
        }

        // Resolve which networks this admin can grant net_user on.
        // Portal admins can target any network; network admins are limited
        // to networks where their binding has the `network:manage` scope.
        const adminGrants = await getActiveScopeGrantsForUser(effectiveUserId);
        const managedNetworkIds = caps.isAdmin
            ? null // unrestricted
            : Array.from(new Set(
                adminGrants
                    .filter((g) => g.networkId && !g.plantMemberNr && g.scopes.includes('network:manage'))
                    .map((g) => g.networkId as number),
            ));

        // Decide which network the new user gets net_user on.
        // - networkId provided → use it (after verifying the admin manages it)
        // - exactly one managed network → auto-pick it
        // - portal admin without networkId → no auto network, user is created
        //   with no network binding (admin can add later via UI)
        // - multiple managed networks without networkId → error, UI must pick
        let resolvedNetworkId: number | null = null;
        if (typeof data.networkId === 'number') {
            if (managedNetworkIds && !managedNetworkIds.includes(data.networkId)) {
                return failure('Keine Berechtigung für dieses Netz');
            }
            resolvedNetworkId = data.networkId;
        } else if (managedNetworkIds && managedNetworkIds.length === 1) {
            resolvedNetworkId = managedNetworkIds[0];
        } else if (managedNetworkIds && managedNetworkIds.length > 1) {
            return failure('Bitte ein Netz auswählen');
        }

        const email = data.email.trim().toLowerCase();
        const name = buildDisplayName(data.firstName, data.lastName);
        if (!name) return failure('Vorname und Nachname sind erforderlich');

        // Abschluss-Review 2026-07-29 (I2, zweiter Teil): Getrimmt wurde
        // bisher, geprüft nicht — `email: ''` war auch über diesen Weg
        // anlegbar. Begründung wie in `createUser`.
        if (!isPlausibleEmailAddress(email)) {
            return failure(EMAIL_INVALID_MESSAGE);
        }

        const existingEmail = await prisma.user.findUnique({ where: { email } });
        if (existingEmail) return failure('E-Mail wird bereits verwendet');

        const usernameInput = data.username?.trim() ?? '';
        const username = usernameInput || await findAvailableUsername(buildUsernameBase(data.firstName, data.lastName));
        if (!username) return failure('Benutzername ist erforderlich');

        const existingUsername = await prisma.user.findUnique({ where: { username } });
        if (existingUsername) return failure('Benutzername ist bereits vergeben');

        const hashedPassword = data.password ? await bcrypt.hash(data.password, SALT_ROUNDS) : undefined;
        // Code-review C2 (2026-07-07): `emailVerified` kam bisher aus dem
        // Client-Input (`data.emailVerified ?? false`). Ein Netz-Admin konnte
        // damit ein Konto mit fremder E-Mail sofort als "bestätigt" anlegen und
        // das Verifikations-Gate umgehen. Im Netz-Admin-Pfad gilt jetzt hart:
        // neue Nutzer sind IMMER unverifiziert und erhalten eine
        // Bestätigungsmail. Auto-Verifizierung bleibt Portal-Admins über
        // `createUser` vorbehalten.
        const newUser = await prisma.user.create({
            data: {
                name,
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                email,
                password: hashedPassword,
                username,
                emailVerified: null,
                creationSource: 'network_admin',
                createdById: effectiveUserId,
            }
        });

        // Create credential account entry for Better Auth
        if (hashedPassword) {
            await prisma.account.create({
                data: {
                    userId: newUser.id,
                    accountId: String(newUser.id),
                    providerId: 'credential',
                    provider: 'credential',
                    providerAccountId: String(newUser.id),
                    type: 'credential',
                    password: hashedPassword,
                },
            });
        }

        // Assign net_user role on the resolved network. We treat a failure
        // here as fatal (instead of swallowing as before) — silently dropping
        // the role binding is exactly the bug Thomas Handler hit, where a
        // newly-created user got no network access and disappeared from the
        // admin's user list.
        if (resolvedNetworkId !== null) {
            const netUserRole = await prisma.role.findUnique({ where: { keyName: 'net_user' } });
            if (!netUserRole) {
                void safeLogSystemError('net_user role missing — cannot bind newly-created user', undefined, { userId: newUser.id });
                return failure('Rolle "net_user" nicht gefunden — bitte Administrator informieren.');
            }
            try {
                await prisma.accessBinding.create({
                    data: {
                        userId: newUser.id,
                        roleId: netUserRole.id,
                        networkId: resolvedNetworkId,
                        plantMemberNr: null,
                        createdById: effectiveUserId,
                        validFrom: new Date(),
                        notes: JSON.stringify({ createdByNetworkAdmin: true }),
                    },
                });
            } catch (bindingError) {
                void safeLogSystemError('Failed to attach net_user binding', bindingError, {
                    userId: newUser.id,
                    networkId: resolvedNetworkId,
                });
                return failure('Benutzer wurde angelegt, aber die Netz-Zuweisung ist fehlgeschlagen. Bitte erneut zuweisen.');
            }
        }

        if (newUser.email) {
            const token = await createEmailVerificationToken(newUser.id);
            const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
            const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;
            const branding = await resolveMailBranding({ networkId: resolvedNetworkId });
            await sendEmail({
                to: newUser.email,
                subject: 'Bitte E-Mail-Adresse bestaetigen',
                text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
                html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href="${verificationUrl}">${verificationUrl}</a></p>`,
                senderNamePostfix: branding.senderNamePostfix,
                signature: branding.signature,
            });
        }

        revalidatePath('/admin/users', 'layout');

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.create',
                resourceType: 'User',
                resourceId: newUser.id,
                newValue: {
                    name: newUser.name,
                    email: newUser.email,
                    username: newUser.username,
                    creationSource: 'network_admin',
                },
            });
        }

        return ok({ userId: newUser.id });
    } catch (e) {
        console.error(e);
        void safeLogSystemError('Network admin user create failed', e, { email: data.email });
        return failure('Fehler beim Erstellen des Benutzers');
    }
}

export async function updateUserAdmin(data: { id: number; firstName: string; lastName: string; email: string; username: string; password?: string; emailVerified?: boolean; expectedUpdatedAt?: string | Date | null; address?: string; postalCode?: string; town?: string; phone?: string; customerNumber?: string }): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Keine Berechtigung');

        const targetUser = await prisma.user.findUnique({ where: { id: data.id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');

        // Kundenidentität Paket 1, Review 2026-07-26 (F1): die vierte Tür.
        //
        // `deleteUser` verschiebt die Adresse nach `deletedEmail` und setzt
        // `email = null`. Die Zammad-Bindung bleibt dabei AKTIV — die Spec
        // lässt Soft-Delete die Bindung ausdrücklich bestehen, und der
        // Backfill bindet soft-gelöschte Konten über `deletedEmail` mit
        // `state = 'active'`. Ohne diesen Riegel sähe `isBlockedEmailChange`
        // unten ein `current = null`, hielte das für eine Erstbefüllung und
        // liesse die Adresse setzen: Eine aktiv gebundene Supportidentität
        // trüge im Portal eine andere Adresse als in Zammad — genau der
        // stille Schaden, den Paket 1 anhält.
        //
        // Gewählt ist der Riegel am ganzen Konto und nicht der engere Griff
        // `isBlockedEmailChange(targetUser.email ?? targetUser.deletedEmail, ...)`.
        // Gründe:
        //  1. Der engere Griff liesse die UNVERÄNDERTE Alt-Adresse weiterhin
        //     durch und schriebe sie nach `email` zurück. Damit wäre sie
        //     wieder vergeben, und `restoreUser` — das vor dem
        //     Wiederherstellen prüft, ob `deletedEmail` schon belegt ist —
        //     fände das Konto selbst und verweigerte die Wiederherstellung
        //     dauerhaft. Ein Fix, der einen zweiten Schaden anlegt.
        //  2. `restoreUser` überschreibt `email` und `username` ohnehin
        //     wieder aus `deletedEmail`/`deletedUsername`. Jede Bearbeitung
        //     dieser Felder an einem gelöschten Konto ist also entweder
        //     wirkungslos oder schädlich; ein ehrlicher Fehler ist besser als
        //     eine still verworfene Änderung.
        //  3. Der Papierkorb bietet nur Wiederherstellen und endgültiges
        //     Löschen, der Bearbeiten-Dialog zeigt nur nicht-gelöschte
        //     Konten. Es gibt also keinen Bedienweg, der hier etwas verliert.
        // `deleteUser` (bereits gelöscht) und `suspendUser` (gelöscht) prüfen
        // denselben Zustand bereits; diese Action war die Ausnahme.
        if (targetUser.deletedAt) {
            return failure('Gelöschte Benutzer können nicht bearbeitet werden. Bitte zuerst wiederherstellen.');
        }

        // Codex M2 (2026-05-11): optimistic locking. Caller may pass the
        // `updatedAt` it saw when opening the edit form; if it doesn't
        // match anymore another admin has modified the user in the
        // meantime and our write would silently clobber their changes.
        // Opt-in to keep older callers working — when the caller omits
        // `expectedUpdatedAt` we fall back to last-write-wins behaviour.
        if (data.expectedUpdatedAt !== undefined && data.expectedUpdatedAt !== null) {
            const expected = data.expectedUpdatedAt instanceof Date
                ? data.expectedUpdatedAt
                : new Date(data.expectedUpdatedAt);
            if (Number.isFinite(expected.getTime())
                && targetUser.updatedAt.getTime() !== expected.getTime()) {
                return failure('Die Daten wurden zwischenzeitlich von einer anderen Sitzung geändert. Bitte laden Sie die Seite neu und versuchen Sie es erneut.');
            }
        }

        const username = data.username.trim();
        if (!username) return failure('Benutzername ist erforderlich');

        // Konsistent zu updateProfile: ein Username mit '@'/Leerzeichen würde
        // den Benutzername-Login unmöglich machen (Kollision mit E-Mail-Erkennung).
        if (!/^[a-zA-Z0-9._-]+$/.test(username)) {
            return failure('Benutzername darf nur Buchstaben, Zahlen, Punkte, Bindestriche und Unterstriche enthalten');
        }

        const name = buildDisplayName(data.firstName, data.lastName);
        if (!name) return failure('Vorname und Nachname sind erforderlich');

        if (username !== targetUser.username) {
            const existingUsername = await prisma.user.findUnique({ where: { username } });
            if (existingUsername) return failure('Benutzername ist bereits vergeben');
        }

        // Kundenidentität Paket 1 (Spec 2026-07-26): Auch der Admin-Pfad
        // tauscht keine aktive Adresse mehr. Er synchronisiert Zammad
        // genauso wenig wie die Selbstbedienung und richtet denselben
        // stillen Schaden an — die Ticketliste des Kunden ist danach leer,
        // und sein nächstes Ticket erzeugt einen zweiten Zammad-Kunden.
        // „Der Admin-Pfad ist kein sicherer Ersatz" ist eine Feststellung,
        // keine Sperre; deshalb steht sie hier.
        // Koordinierter Wechsel: docs/operations/zammad-email-wechsel-manuell.md
        //
        // Review-Nachfassung 2026-07-27 (Blocker 2), die fünfte Tür: Die
        // Sperre allein genügte NICHT, weil diese Action keine
        // serverseitige Feldprüfung hatte und den Rohwert schrieb. Damit war
        // der Wechsel in zwei Aufrufen möglich:
        //   1. `email: ''` — `isBlockedEmailChange` sieht keinen Wechsel
        //      (eine leere Eingabe ist kein Antrag auf eine andere Adresse),
        //      und die leere Zeichenkette landete in der Datenbank.
        //   2. `email: 'neu@example.at'` — die jetzt leere aktuelle Adresse
        //      normalisiert zu `null`, der Aufruf gilt als Erstbefüllung und
        //      geht durch. Portaladresse gewechselt, Zammad ahnungslos.
        // Die Reihenfolge unten ist deshalb: Pflichtfeld, dann Sperre, dann
        // Format. Ein ausgeblendetes oder als `required` markiertes
        // Formularfeld hält keine Server Action auf.
        const requestedEmail = normalizeEmail(data.email);
        const currentEmail = normalizeEmail(targetUser.email);

        // Eine vorhandene Adresse zu ENTFERNEN ist genauso ein Eingriff in
        // die Identität wie sie zu tauschen — und war der erste Schritt des
        // Umwegs. Bei einem Konto, das noch nie eine Adresse trug, bleibt
        // ein leeres Feld dagegen erlaubt: sonst liesse sich dort nicht
        // einmal mehr der Name ändern.
        // Bei einem Konto, das noch nie eine Adresse trug, bleibt ein leeres
        // Feld erlaubt (sonst liesse sich dort nicht einmal mehr der Name
        // ändern); geschrieben wird dann `targetUser.email`, also `null` und
        // NIE die leere Zeichenkette.
        if (requestedEmail === null && currentEmail !== null) {
            return failure(EMAIL_REQUIRED_MESSAGE);
        }

        if (isBlockedEmailChange(targetUser.email, data.email)) {
            return failure(EMAIL_CHANGE_BLOCKED_MESSAGE);
        }

        // Ab hier kann nur noch zweierlei vorliegen: unveränderte Adresse
        // oder Erstbefüllung. Geprüft wird ausschliesslich der SCHREIBENDE
        // Fall — ein Bestand wie `dienst@localhost` darf nicht dazu führen,
        // dass sich das Konto gar nicht mehr bearbeiten lässt.
        const emailChanged = requestedEmail !== currentEmail;
        if (emailChanged && !isPlausibleEmailAddress(requestedEmail)) {
            return failure(EMAIL_INVALID_MESSAGE);
        }

        // Unverändert heisst unverändert: Bei bloss abweichender Schreibweise
        // bleibt der gespeicherte Wert stehen, statt still überschrieben zu
        // werden.
        const nextEmail = emailChanged ? requestedEmail : targetUser.email;

        if (emailChanged && nextEmail !== null) {
            const existing = await prisma.user.findUnique({ where: { email: nextEmail } });
            if (existing) return failure('Email wird bereits verwendet');
        }

        // Review-Nachfassung 2026-07-25 (Problem 3), Update-Seite.
        //
        // Geschlossen wird der STILLE Pfad: Ein Adresswechsel setzte bisher
        // `emailVerified: new Date()` — die NEUE, nie geprüfte Adresse galt
        // sofort als bestätigt, ohne dass irgendjemand das entschieden hätte.
        // Genau das ist die A1-Aussage über ein fremdes Postfach, nur über die
        // Update- statt die Create-Action. Adresswechsel setzt die Bestätigung
        // deshalb IMMER zurück und löst eine neue Bestätigungsmail aus.
        //
        // Bewusst NICHT entfernt: die ausdrückliche Freigabe durch einen
        // Portal-Admin bei UNVERÄNDERTER Adresse (`emailVerified: true`).
        // Begründung: Konten ohne erreichbares Postfach (Test-, Dienst- und
        // Übergabekonten) muss der Betreiber weiterhin freischalten können —
        // sonst weicht er auf direkte DB-Eingriffe aus, die niemand sieht.
        // Sie ist jetzt eine bewusste Einzelhandlung im Bearbeiten-Dialog
        // statt eine Vorbelegung bei der Anlage, und sie steht mit Alt- und
        // Neuwert im Audit-Log (siehe logAuditEvent unten).
        // RESTRISIKO, dokumentiert: ein Portal-Admin kann damit weiterhin eine
        // fremde Adresse als bestätigt erklären und über den Zammad-SSO als
        // diese Person auftreten. Das ist die Vertrauensstufe „Portal-Admin",
        // nicht mehr die eines Netz-Admins (C2) und keine Vorbelegung mehr.
        // Wer auch das ausschliessen will, ersetzt die Bedingung unten durch
        // `Boolean(targetUser.emailVerified)` — der Rest der Logik bleibt.
        const grantRequested = data.emailVerified === true;
        const keepVerified =
            data.emailVerified !== false
            && !emailChanged
            && (Boolean(targetUser.emailVerified) || grantRequested);
        // `shouldVerifyEmail` steuert unten zusätzlich den Mailversand:
        // wer nicht (mehr) bestätigt ist, bekommt einen frischen Link.
        const shouldVerifyEmail = keepVerified;

        const updateData: {
            name: string; firstName: string; lastName: string; email: string | null; username: string;
            emailVerified?: Date | null; password?: string;
            address?: string | null; postalCode?: string | null; town?: string | null;
            phone?: string | null; customerNumber?: string | null;
        } = {
            name,
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: nextEmail,
            username,
            // Ein bereits gesetzter Zeitstempel bleibt unverändert (kein
            // stilles Verjüngen); nur die ausdrückliche Freigabe eines bisher
            // unbestätigten Kontos schreibt „jetzt".
            emailVerified: keepVerified ? (targetUser.emailVerified ?? new Date()) : null,
        };

        // Kontaktfelder (Spec 6/11.3): undefined = unverändert, '' = leeren.
        // customerNumber ist hier zulässig, weil diese Action bereits oben
        // portal.manage erzwingt — die Selbstbedienung (updateProfile) kennt
        // das Feld nicht (Whitelist).
        if (data.address !== undefined) updateData.address = data.address.trim() || null;
        if (data.postalCode !== undefined) updateData.postalCode = data.postalCode.trim() || null;
        if (data.town !== undefined) updateData.town = data.town.trim() || null;
        if (data.phone !== undefined) {
            const trimmedPhone = data.phone.trim();
            if (!trimmedPhone) {
                updateData.phone = null;
            } else {
                const normalizedPhone = normalizePhone(trimmedPhone);
                if (!normalizedPhone) return failure('Ungültige Telefonnummer');
                updateData.phone = normalizedPhone;
            }
        }
        if (data.customerNumber !== undefined) {
            updateData.customerNumber = data.customerNumber.trim() || null;
        }

        // Leeres/whitespace-only Passwort = KEINE Aenderung. Nur wenn ein
        // echtes Passwort gesetzt wird, pruefen wir die Mindestlaenge und
        // schreiben spaeter user.password UND die Better-Auth-Credential.
        let hashedPassword: string | undefined;
        if (data.password && data.password.trim().length > 0) {
            if (data.password.length < MIN_PASSWORD_LENGTH) {
                return failure(`Das Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
            }
            hashedPassword = await bcrypt.hash(data.password, SALT_ROUNDS);
            updateData.password = hashedPassword;
        }

        // user.password UND die Better-Auth-Credential (account, providerId
        // 'credential') muessen atomar zusammen geschrieben werden — sonst
        // veraltet die Credential und der Login mit dem neuen Passwort schlaegt
        // fehl. Muster gespiegelt aus resetPasswordWithToken.
        try {
            await prisma.$transaction(async (tx) => {
                // When expectedUpdatedAt is provided we also gate the WRITE with
                // the same field so a concurrent update slipping in between the
                // SELECT above and the UPDATE here cannot pass the check.
                if (data.expectedUpdatedAt !== undefined && data.expectedUpdatedAt !== null) {
                    const expected = data.expectedUpdatedAt instanceof Date
                        ? data.expectedUpdatedAt
                        : new Date(data.expectedUpdatedAt);
                    if (Number.isFinite(expected.getTime())) {
                        const guardedUpdate = await tx.user.updateMany({
                            where: { id: data.id, updatedAt: expected },
                            data: updateData,
                        });
                        if (guardedUpdate.count !== 1) {
                            throw new ConcurrentUpdateError();
                        }
                    } else {
                        await tx.user.update({ where: { id: data.id }, data: updateData });
                    }
                } else {
                    await tx.user.update({ where: { id: data.id }, data: updateData });
                }

                // Ensure credential account exists for Better Auth login
                if (hashedPassword) {
                    const existing = await tx.account.findFirst({
                        where: { userId: data.id, providerId: 'credential' },
                    });
                    if (existing) {
                        await tx.account.update({
                            where: { id: existing.id },
                            data: { password: hashedPassword },
                        });
                    } else {
                        await tx.account.create({
                            data: {
                                userId: data.id,
                                accountId: String(data.id),
                                providerId: 'credential',
                                provider: 'credential',
                                providerAccountId: String(data.id),
                                type: 'credential',
                                password: hashedPassword,
                            },
                        });
                    }
                }
            });
        } catch (txError) {
            if (txError instanceof ConcurrentUpdateError) {
                return failure('Die Daten wurden zwischenzeitlich von einer anderen Sitzung geändert. Bitte laden Sie die Seite neu und versuchen Sie es erneut.');
            }
            throw txError;
        }

        // F-4 (Review 2026-07-08): Admin-Passwort-Set widerruft alle Sessions
        // des Ziel-Nutzers — Redis-autoritativ via revokeUserSessions
        // (A1-Muster, auth-revocation.ts). Setzt der Admin sein EIGENES
        // Passwort, bleibt die aktuelle Session erhalten (exceptToken),
        // identisch zur Selbstbedienungs-Änderung in changePassword.
        //
        // Review-Nachfassung 2026-07-25 (Problem 1, Ursachenseite): dasselbe
        // gilt, wenn die Bestätigung verloren geht (entzogen oder Adresse
        // gewechselt). Der session.create-Hook greift nur bei der
        // ERSTELLUNG — ohne Widerruf lebt die Session des jetzt
        // unbestätigten Kontos weiter, und genau diese Kombination
        // (gültige Session + nicht SSO-berechtigt) war der Auslöser der
        // Redirect-Schleife. Zustand und Sessions bleiben so deckungsgleich.
        const verificationLost = Boolean(targetUser.emailVerified) && !keepVerified;
        if (hashedPassword || verificationLost) {
            // A-5 (Review 2026-07-08): exceptToken NUR wenn der REALE Akteur
            // (sessionUserId) sein eigenes Konto editiert — nicht der
            // impersonierte effectiveUserId. Sonst würde sich ein Admin während
            // einer Impersonation beim Editieren des eigenen Kontos aus der
            // aktuellen (real ihm gehörenden) Session ausloggen.
            const currentToken = data.id === sessionUserId
                ? (session as { session?: { token?: string } } | null)?.session?.token
                : undefined;
            await revokeUserSessions(
                data.id,
                currentToken ? { exceptToken: currentToken } : undefined,
            );
        }

        const updatedUser = await prisma.user.findUniqueOrThrow({ where: { id: data.id } });

        if (updatedUser.email && !shouldVerifyEmail) {
            const token = await createEmailVerificationToken(updatedUser.id);
            const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
            const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;
            // WICHTIG (Spec, Fehlerfälle): userId des ZIEL-Users — nie die des
            // Admins, sonst stünde das Admin-Netz im From einer Kunden-Mail.
            const branding = await resolveMailBranding({ userId: updatedUser.id });
            const emailResult = await sendEmail({
                to: updatedUser.email,
                subject: 'Bitte E-Mail-Adresse bestaetigen',
                text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
                html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href=\"${verificationUrl}\">${verificationUrl}</a></p>`,
                senderNamePostfix: branding.senderNamePostfix,
                signature: branding.signature,
            });
            if (emailResult.error) {
                return failure(emailResult.error);
            }
        }


        revalidatePath('/admin/users', 'layout');

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.update',
                resourceType: 'User',
                resourceId: data.id,
                oldValue: { name: targetUser.name, firstName: targetUser.firstName, lastName: targetUser.lastName, email: targetUser.email, username: targetUser.username, emailVerified: targetUser.emailVerified },
                newValue: { name: updateData.name, firstName: updateData.firstName, lastName: updateData.lastName, email: updateData.email, username: updateData.username, emailVerified: updateData.emailVerified },
                // Ein Admin-Passwort-Set muss nachvollziehbar sein — nur das
                // Faktum protokollieren, niemals Passwort/Hash.
                metadata: { passwordChanged: Boolean(hashedPassword) },
            });
        }

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User update failed', e, { userId: data.id });
        return failure('Fehler beim Aktualisieren des Benutzers');
    }
}

export async function deleteUser(id: number): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Keine Berechtigung');

        if (id === effectiveUserId) return failure('Sie können sich nicht selbst löschen');

        const targetUser = await prisma.user.findUnique({ where: { id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');
        if (targetUser.deletedAt) return failure('Benutzer ist bereits gelöscht');

        await prisma.$transaction([
            prisma.user.update({
                where: { id },
                data: {
                    deletedAt: new Date(),
                    deletedById: effectiveUserId,
                    deletedEmail: targetUser.email,
                    deletedUsername: targetUser.username,
                    email: null,
                    username: null,
                    externalId: null,
                    pendingEmail: null,
                },
            }),
            prisma.accessBinding.deleteMany({ where: { userId: id } }),
            prisma.groupMembership.deleteMany({ where: { userId: id } }),
            prisma.report.deleteMany({ where: { userId: id } }),
            prisma.account.deleteMany({ where: { userId: id } }),
            prisma.passkey.deleteMany({ where: { userId: id } }),
            prisma.emailVerificationToken.deleteMany({ where: { userId: id } }),
            prisma.passwordResetToken.deleteMany({ where: { userId: id } }),
        ]);

        // Review A1: Sessions liegen im Better-Auth secondaryStorage (Redis) —
        // das frühere prisma.session.deleteMany traf sie nicht. Erst NACH dem
        // Commit widerrufen: der deletedAt-Zustand steht dann fest und der
        // session.create.before-Hook (A3) blockt jeden Re-Login.
        await revokeUserSessions(id);

        // Clear all caches so stale permissions don't leak
        invalidateGrantCache(id);
        void invalidateCapabilityCache(id);
        await CacheInvalidation.user(id);

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.soft_delete',
                resourceType: 'User',
                resourceId: id,
                metadata: {
                    deletedUser: {
                        name: targetUser.name,
                        email: targetUser.email,
                        username: targetUser.username,
                    },
                },
            });
        }

        revalidatePath('/admin/users', 'layout');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User soft-delete failed', e, { userId: id });
        return failure('Fehler beim Löschen des Benutzers');
    }
}

export async function suspendUser(id: number): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');

        // Only portal admins may suspend users
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Nur Portal-Administratoren dürfen Benutzer deaktivieren');

        if (id === effectiveUserId) return failure('Sie können sich nicht selbst deaktivieren');

        const targetUser = await prisma.user.findUnique({ where: { id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');
        if (targetUser.deletedAt) return failure('Benutzer ist gelöscht');
        if (targetUser.suspendedAt) return failure('Benutzer ist bereits deaktiviert');

        await prisma.$transaction([
            prisma.user.update({
                where: { id },
                data: {
                    suspendedAt: new Date(),
                    suspendedById: effectiveUserId,
                },
            }),
        ]);

        // Review A1: Redis-Sessions über Better-Auth widerrufen — DB-only-Delete
        // ließe die aktive Session bis zum natürlichen Ablauf weiterleben.
        await revokeUserSessions(id);

        // Clear caches so suspended user loses access immediately
        invalidateGrantCache(id);
        void invalidateCapabilityCache(id);
        await CacheInvalidation.user(id);

        await logAuditEvent({
            userId: effectiveUserId,
            action: 'user.suspend',
            resourceType: 'User',
            resourceId: id,
            metadata: {
                suspendedUser: {
                    name: targetUser.name,
                    email: targetUser.email,
                    username: targetUser.username,
                },
            },
        });

        revalidatePath('/admin/users', 'layout');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User suspend failed', e, { userId: id });
        return failure('Fehler beim Deaktivieren des Benutzers');
    }
}

export async function unsuspendUser(id: number): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');

        // Only portal admins may unsuspend users
        const portalDecision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!portalDecision.allowed) return failure('Nur Portal-Administratoren dürfen Benutzer aktivieren');

        const targetUser = await prisma.user.findUnique({ where: { id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');
        if (!targetUser.suspendedAt) return failure('Benutzer ist nicht deaktiviert');

        await prisma.user.update({
            where: { id },
            data: {
                suspendedAt: null,
                suspendedById: null,
            },
        });

        await logAuditEvent({
            userId: effectiveUserId,
            action: 'user.unsuspend',
            resourceType: 'User',
            resourceId: id,
            metadata: {
                unsuspendedUser: {
                    name: targetUser.name,
                    email: targetUser.email,
                    username: targetUser.username,
                },
            },
        });

        revalidatePath('/admin/users', 'layout');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User unsuspend failed', e, { userId: id });
        return failure('Fehler beim Aktivieren des Benutzers');
    }
}

export async function restoreUser(id: number, options?: { restorePermissions?: boolean }): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Keine Berechtigung');

        const targetUser = await prisma.user.findUnique({ where: { id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');
        if (!targetUser.deletedAt) return failure('Benutzer ist nicht gelöscht');

        if (targetUser.deletedEmail) {
            const emailConflict = await prisma.user.findUnique({ where: { email: targetUser.deletedEmail } });
            if (emailConflict) return failure('Die E-Mail-Adresse wird bereits von einem anderen Benutzer verwendet');
        }

        if (targetUser.deletedUsername) {
            const usernameConflict = await prisma.user.findUnique({ where: { username: targetUser.deletedUsername } });
            if (usernameConflict) return failure('Der Benutzername wird bereits von einem anderen Benutzer verwendet');
        }

        const restorePermissions = options?.restorePermissions ?? true;

        await prisma.$transaction(async (tx) => {
            await tx.user.update({
                where: { id },
                data: {
                    deletedAt: null,
                    deletedById: null,
                    email: targetUser.deletedEmail,
                    username: targetUser.deletedUsername,
                    deletedEmail: null,
                    deletedUsername: null,
                },
            });

            if (!restorePermissions) {
                await tx.accessBinding.deleteMany({ where: { userId: id } });
                await tx.groupMembership.deleteMany({ where: { userId: id } });
                await tx.report.deleteMany({ where: { userId: id } });
            }
        });

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.restore',
                resourceType: 'User',
                resourceId: id,
                metadata: {
                    restoredUser: {
                        name: targetUser.name,
                        email: targetUser.deletedEmail,
                        username: targetUser.deletedUsername,
                    },
                    restorePermissions,
                },
            });
        }

        revalidatePath('/admin/users', 'layout');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User restore failed', e, { userId: id });
        return failure('Fehler beim Wiederherstellen des Benutzers');
    }
}

export async function purgeUser(id: number): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Keine Berechtigung');

        const targetUser = await prisma.user.findUnique({ where: { id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');
        if (!targetUser.deletedAt) return failure('Nur gelöschte Benutzer können endgültig entfernt werden');

        // Kundenidentität Paket 1: Ablösung VOR dem Löschen, beides in einer
        // Transaktion. Ohne die Ablösung scheitert der Purge am
        // Restrict-Fremdschlüssel der Bindung — das ist so gewollt, denn ein
        // Zammad-Kunde ohne Grabstein würde bei der nächsten E-Mail-Suche
        // wieder gebunden. Und ohne die Transaktion bliebe bei einem Fehler
        // beim Löschen eine abgelöste Bindung zu einem noch lebenden Konto
        // zurück: der Nutzer verlöre seine Supportidentität, ohne dass
        // jemand gelöscht hätte.
        await prisma.$transaction(async (tx) => {
            await detachZammadBinding(tx, id, `user.purge durch User ${effectiveUserId}`);
            await tx.user.delete({ where: { id } });
        });

        invalidateGrantCache(id);
        void invalidateCapabilityCache(id);
        await CacheInvalidation.user(id);

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.purge',
                resourceType: 'User',
                resourceId: id,
                metadata: {
                    purgedUser: {
                        name: targetUser.name,
                        deletedEmail: targetUser.deletedEmail,
                        deletedUsername: targetUser.deletedUsername,
                    },
                },
            });
        }

        revalidatePath('/admin/users', 'layout');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User purge failed', e, { userId: id });
        return failure('Fehler beim endgültigen Löschen des Benutzers');
    }
}

export async function updateProfile(data: {
    firstName: string; lastName: string; email: string; username: string;
    address?: string; postalCode?: string; town?: string; phone?: string;
    // customerNumber BEWUSST NICHT in der Signatur — Admin-only (Spec 6).
}): Promise<ActionResult<{
    emailVerified: Date | null;
    pendingEmail: string | null;
    /** Die nach dem Speichern GÜLTIGE Adresse (M4, siehe unten am `ok(...)`). */
    email: string | null;
}>> {
    try {
        const userId = await getCurrentUserId();
        if (!userId) return failure('Nicht eingeloggt');

        const currentUser = await prisma.user.findUnique({ where: { id: userId } });
        if (!currentUser) return failure('User nicht gefunden');

        const username = data.username.trim();
        if (!username) return failure('Benutzername ist erforderlich', undefined, { field: 'username' });

        // Validate username format (alphanumeric, dots, hyphens, underscores)
        if (!/^[a-zA-Z0-9._-]+$/.test(username)) {
            return failure('Benutzername darf nur Buchstaben, Zahlen, Punkte, Bindestriche und Unterstriche enthalten', undefined, { field: 'username' });
        }

        if (!data.firstName?.trim()) return failure('Vorname ist erforderlich', undefined, { field: 'firstName' });
        if (!data.lastName?.trim()) return failure('Nachname ist erforderlich', undefined, { field: 'lastName' });
        const name = buildDisplayName(data.firstName, data.lastName);
        // Hausmuster wie in `registerUser` und `updateUserAdmin`. Die beiden
        // Prüfungen darüber machen `null` hier zwar unerreichbar — aber die
        // Zusicherung ruht dann auf einer Vorbedingung ausserhalb dieser
        // Zeile, deren Wegfall keinen Übersetzungsfehler auslöst. Diese
        // stille Kopplung ist die Fehlerklasse, gegen die dieser Task
        // angetreten ist; deshalb steht hier eine Laufzeitprüfung statt `!`.
        if (!name) return failure('Vorname und Nachname sind erforderlich');

        if (username !== currentUser.username) {
            const existingUsername = await prisma.user.findUnique({ where: { username } });
            if (existingUsername) return failure('Benutzername wird bereits verwendet', undefined, { field: 'username' });
        }

        const normalizedEmail = data.email.trim().toLowerCase();
        const currentEmail = currentUser.email?.trim().toLowerCase() ?? '';
        const currentPendingEmail = currentUser.pendingEmail?.trim().toLowerCase() ?? null;

        // E-Mail-Selbstservice (Plan 2026-07-29): Die pauschale Paket-1-Sperre
        // ist dem fail-closed Gate gewichen. Freigegeben wird NUR der
        // nachweislich harmlose Fall ('sofort-frei': keine Zammad-Bindung,
        // keine Supporthistorie, Zieladresse frei) — er mündet in den
        // bestehenden pendingEmail-und-Bestätigungslink-Fluss weiter unten.
        // Gebundene Kunden ('nur-mit-zammad-abgleich') bleiben bis zur
        // separaten Freigabe des synchronen Zammad-Wegs beim Support.
        //
        // DIE WECHSEL-ERKENNUNG DAVOR IST NICHT OPTIONAL. Bei
        // `currentEmail === targetEmail` liefert das Gate für ein gebundenes
        // Konto `gesperrt` — es beurteilt die Lage des Kontos, nicht die
        // Differenz der beiden Adressen. Ein unbedingter Aufruf nähme den
        // gebundenen Konten (5 von 19 auf der Produktion) das Speichern ihres
        // Profils insgesamt, auch ohne jede Adressänderung. Deshalb erst
        // `isBlockedEmailChange` — „wurde überhaupt ein Wechsel beantragt?" —
        // und nur dann das Gate.
        //
        // `art !== 'gesperrt'` wäre KEINE Freigabe: Für
        // 'nur-mit-zammad-abgleich' gibt es noch keinen Code, der Zammad
        // mitzieht. Das `switch` ist deshalb ausgeschrieben, samt
        // `default: assertNever` — das Typsystem erzwingt die Vollständigkeit
        // nicht (nachgeprüft, Review 2026-07-29), der `default`-Arm schon.
        //
        // Der Ablehnungs-GRUND bleibt im Protokoll und kommt nie hierher: Er
        // nennt fremde Konten und Zammad-Kunden-IDs und wäre am Client eine
        // Auskunft über fremde Identitäten. `pruefeEmailWechselOderSperren`
        // protokolliert ihn genau einmal und gibt ihn gar nicht erst zurück.
        // AUFLAGE 1 (Nachprüfung 2026-07-29): Die ERSTBEFÜLLUNG umging das Gate
        // vollständig, und die zweite Sicherung greift dort nicht.
        //
        // `isBlockedEmailChange` meldet für ein Konto ohne Adresse keinen
        // Wechsel — richtig, denn ein nie befülltes Konto hat keine
        // Zammad-Identität, die auseinanderlaufen könnte. Für `email = ''` und
        // `email = null` ist aber `!currentUser.email` wahr, und der
        // Erstbefüllungszweig unten schreibt die Adresse SOFORT, mit
        // `pendingEmail = null` und ohne Bestätigungsschritt. Es gibt danach
        // kein `verifyEmailToken` mehr, in dem das zweite Gate greifen könnte;
        // die Entwarnung „das zweite Gate fängt das ab" gilt ausschliesslich
        // für `email = '   '` (Leerraum ist truthy und landet im
        // pendingEmail-Zweig).
        //
        // Ein gebundenes Bestandskonto mit leerer Adresse hätte damit in einem
        // einzigen Speichern eine beliebige neue Portaladresse bekommen,
        // während Zammad die alte weiterführt — genau der stille Schaden, den
        // dieses Vorhaben verhindern soll.
        //
        // Die Wege, die `''` schrieben, sind zu — aber erst seit dem
        // Abschluss-Review 2026-07-29, nicht seit 2026-07-27 (die frühere
        // Fassung dieses Satzes war falsch): `updateUserAdmin` seit dem
        // 27.07., `createUser` und `createUserAsNetworkAdmin` erst jetzt
        // (beide schrieben die Eingabe ungeprüft, siehe dort und
        // `__tests__/create-user-email-formal.test.ts`). `registerUser` wies
        // die leere Adresse seit jeher ab. Es geht ab hier tatsächlich nur
        // noch um Bestandszeilen — auf dk9 sind das null.
        //
        // Die Bindungszeile (JEDER Zustand, nicht nur `active`) ist die
        // Vorfrage: Existiert sie, muss auch die Erstbefüllung durchs Gate.
        // Sie wird nur für eine tatsächliche Erstbefüllung abgefragt — ein
        // Speichern ohne Adressänderung kostet weiterhin keine einzige
        // zusätzliche Abfrage.
        //
        // Praktische Folge, bewusst so: Für ein gebundenes Konto liefert das
        // Gate bei leerer aktueller Adresse immer `gesperrt` („nicht
        // normalisierbar"). Ein solches Konto bekommt seine Adresse also nur
        // noch über den Support. Das ist die richtige Richtung — der
        // Bestandszustand ist selbst schon ein Betriebsfehler.
        const wechselBeantragt = isBlockedEmailChange(currentUser.email, normalizedEmail);

        // Mengenbegrenzung (Blocker-Behebung 2026-07-30). Sie steht HIER —
        // vor dem Gate, vor der Kollisionsprüfung, vor jedem Schreibvorgang —
        // und das ist keine Stilfrage:
        //
        //  * Erst ab hier steht fest, dass überhaupt ein Versand beantragt
        //    ist. Weiter oben (Namensfelder, Benutzername) würde ein blosses
        //    Speichern von Telefonnummer oder Anschrift Versandbudget kosten.
        //  * Weiter unten wäre es zu spät: Gate und Kollisionsprüfung
        //    entscheiden ANHAND DER ZIELADRESSE. Läge der Zähler dahinter,
        //    kostete eine Ablehnung nichts — und das bekannte
        //    Verhaltensorakel („Ablehnung heisst: die Adresse gehört schon
        //    jemandem") bliebe unbegrenzt abfragbar. So kostet jeder Versuch
        //    dasselbe, gleich wie er ausgeht.
        //
        // Die Bedingung ist bewusst breiter als `wechselBeantragt`: Auch die
        // ERSTBEFÜLLUNG verschickt eine Bestätigungsmail, und sie ist für
        // `isBlockedEmailChange` kein Wechsel. Umgekehrt ist das erneute
        // Absenden einer bereits vorgemerkten Adresse ausgenommen — dabei
        // wird nichts versendet (siehe `!currentPendingEmail || ...` unten),
        // und es verrät auch nichts, weil der Wert bereits am eigenen Konto
        // steht.
        const versandBeantragt = normalizedEmail.length > 0
            && normalizedEmail !== currentEmail
            && normalizedEmail !== currentPendingEmail;
        if (versandBeantragt && !(await consumeVerificationEmailQuota(currentUser.id))) {
            logger.warning(
                'Wechselantrag abgelehnt: Versandkontingent erschöpft',
                { userId: currentUser.id, pfad: 'updateProfile' },
                'EmailChange',
            );
            return failure(EMAIL_VERIFICATION_RATE_LIMITED_MESSAGE, undefined, { field: 'email' });
        }

        const erstbefuellungGebunden = !wechselBeantragt
            && !currentUser.email
            && normalizedEmail.length > 0
            && (await prisma.zammadCustomerBinding.findUnique({
                where: { portalUserId: currentUser.id },
                select: { id: true },
            })) !== null;

        if (wechselBeantragt || erstbefuellungGebunden) {
            const ausgang = await pruefeEmailWechselOderSperren(
                currentUser.id,
                currentEmail,
                normalizedEmail,
                erstbefuellungGebunden
                    ? { pfad: 'updateProfile', fall: 'erstbefuellung-gebunden' }
                    : { pfad: 'updateProfile' },
            );
            switch (ausgang.art) {
                case 'sofort-frei':
                    break;
                case 'nur-mit-zammad-abgleich':
                    return failure(EMAIL_CHANGE_BLOCKED_MESSAGE, undefined, { field: 'email' });
                case 'gesperrt':
                    return failure(EMAIL_CHANGE_BLOCKED_MESSAGE, undefined, { field: 'email' });
                default:
                    return assertNever(ausgang);
            }
        }

        let pendingEmail = currentUser.pendingEmail ?? null;
        let nextEmail = currentUser.email;
        let nextEmailVerified: Date | null | undefined = currentUser.emailVerified;
        let verificationTargetEmail: string | null = null;
        let pendingCleared = false;

        if (!currentUser.email && normalizedEmail) {
            // Review-Nachfassung 2026-07-27 (Blocker 2), Gegenprobe am
            // Selbstbedienungspfad: Auch hier wurde bisher ungeprüft
            // geschrieben. Ein Wechsel in zwei Aufrufen ist über diese Action
            // nicht möglich (eine leere Eingabe behält die aktuelle Adresse
            // bei und schreibt gar nichts), aber die Erstbefüllung nahm jeden
            // Wert an — `x` wäre eine Adresse gewesen, die niemand bestätigen
            // kann und die das Konto anschliessend an der Sperre festnagelt.
            if (!isPlausibleEmailAddress(normalizedEmail)) {
                return failure(EMAIL_INVALID_MESSAGE, undefined, { field: 'email' });
            }
            const existing = await prisma.user.findFirst({
                where: {
                    OR: [
                        { email: normalizedEmail },
                        { pendingEmail: normalizedEmail },
                    ],
                },
            });
            // Abschluss-Review 2026-07-29 (I2): „Email wird bereits verwendet"
            // war hier ein AUSKUNFTSORAKEL. Der Zweig steht jedem Konto ohne
            // Adresse offen — die Antwort bestätigte für jede beliebig
            // eingegebene Adresse, ob unter ihr ein Portalkonto (oder ein
            // offener Wechsel) geführt wird. Der Wechselzweig 25 Zeilen tiefer
            // beantwortet dieselbe Lage längst neutral; die frühere
            // Zurückstellung („ein Pfad, den diese Aufgabe nicht öffnet")
            // trägt nicht mehr, seit Auflage 1 das Gate an genau diesen Zweig
            // gehängt hat. Der Klartext geht ins Protokoll, wo er hingehört.
            //
            // BENANNTES RESTRISIKO (Nachprüfung 2026-07-29, N3), bewusst NICHT
            // geschlossen: Damit ist die MELDUNG gleich, das VERHALTEN nicht.
            // Belegte Zieladresse → Ablehnung, freie → `success: true`. Weil
            // eine Ablehnung nichts schreibt, ist der Versuch beliebig oft
            // wiederholbar; nur der Treffer verbraucht das Konto. Die Auflage
            // „nie durch unterscheidbare Meldungen" ist erfüllt, „nie durch
            // unterscheidbares Verhalten" nicht.
            //
            // Warum trotzdem nicht hier: Dieselbe Form hat der Wechselzweig
            // seit jeher (und `registerUser` löst sie mit einer EINZIGEN
            // generischen Antwort für alle Ablehnungsgründe — die Vorlage,
            // falls das angegangen wird). Der Befund stammt also nicht aus
            // dieser Fixwelle und gehört als eigener Arbeitsschritt über ALLE
            // drei Pfade gelegt, nicht einseitig hier. Er wäre sonst nur
            // verschoben und sähe geschlossen aus.
            if (existing) {
                logger.warning(
                    'E-Mail-Wechsel abgelehnt: Zieladresse bereits vergeben',
                    {
                        userId: currentUser.id,
                        belegtDurchUserId: existing.id,
                        pfad: 'updateProfile:erstbefuellung',
                    },
                    'EmailChange',
                );
                return failure(EMAIL_CHANGE_BLOCKED_MESSAGE, undefined, { field: 'email' });
            }
            nextEmail = normalizedEmail;
            nextEmailVerified = null;
            pendingEmail = null;
            verificationTargetEmail = normalizedEmail;
        } else if (normalizedEmail && normalizedEmail !== currentEmail) {
            // Abschluss-Review 2026-07-29 (I1): Die Formalprüfung fehlte
            // ausgerechnet im Zweig, den dieses Vorhaben WIEDER GEÖFFNET hat.
            // Unter der Pauschalsperre war er unerreichbar; das Öffnen hat die
            // Prüfung des Erstbefüllungszweigs (16 Zeilen darüber) nicht
            // mitgezogen. Ohne sie schrieb `updateProfile({ email: 'kaputt' })`
            // ein `pendingEmail = 'kaputt'`, schickte den Bestätigungslink an
            // `kaputt` und meldete Erfolg — und das Konto stand danach mit
            // einem Wechsel da, den niemand bestätigen kann.
            //
            // Das HTML-`type="email"` ist KEINE Sicherung: Eine Server Action
            // ist ein öffentlicher Endpunkt. Wörtlich das Argument, mit dem
            // Review 2026-07-27 dieselbe Klasse in `updateUserAdmin` schloss.
            //
            // Steht NACH dem Gate, wie im Erstbefüllungszweig: An einer
            // Autorisierungsgrenze entscheidet zuerst, ob überhaupt gewechselt
            // werden darf; die Form des Wunschwerts ist die zweite Frage.
            if (!isPlausibleEmailAddress(normalizedEmail)) {
                return failure(EMAIL_INVALID_MESSAGE, undefined, { field: 'email' });
            }
            if (!currentPendingEmail || normalizedEmail !== currentPendingEmail) {
                const existing = await prisma.user.findFirst({
                    where: {
                        OR: [
                            { email: normalizedEmail },
                            { pendingEmail: normalizedEmail },
                        ],
                        NOT: { id: currentUser.id },
                    },
                });
                // Kein Auskunftsmittel über fremde Konten (Plan 2026-07-29):
                // Solange der Wechsel gesperrt war, war dieser Zweig
                // unerreichbar. Mit dem wieder geöffneten Selbstservice wäre
                // „Email wird bereits verwendet" die Bestätigung, dass unter
                // der angefragten Adresse ein Portalkonto geführt wird —
                // abfragbar für jede beliebige Adresse. Nach aussen ist das
                // deshalb dieselbe Sperrmeldung wie jede andere Ablehnung; der
                // Klartext geht ins Protokoll. Das Gate sieht diesen Fall
                // NICHT: `isEmailReuseBlocked` prüft nur gelöschte Konten.
                if (existing) {
                    logger.warning(
                        'E-Mail-Wechsel abgelehnt: Zieladresse bereits vergeben',
                        { userId: currentUser.id, belegtDurchUserId: existing.id, pfad: 'updateProfile' },
                        'EmailChange',
                    );
                    return failure(EMAIL_CHANGE_BLOCKED_MESSAGE, undefined, { field: 'email' });
                }
                pendingEmail = normalizedEmail;
                verificationTargetEmail = normalizedEmail;
            }
        } else if (currentPendingEmail) {
            pendingEmail = null;
            pendingCleared = true;
        }

        const profileContactData: {
            address?: string | null; postalCode?: string | null;
            town?: string | null; phone?: string | null;
        } = {};
        if (data.address !== undefined) profileContactData.address = data.address.trim() || null;
        if (data.postalCode !== undefined) profileContactData.postalCode = data.postalCode.trim() || null;
        if (data.town !== undefined) profileContactData.town = data.town.trim() || null;
        if (data.phone !== undefined) {
            const trimmedPhone = data.phone.trim();
            if (!trimmedPhone) {
                profileContactData.phone = null;
            } else {
                const normalizedPhone = normalizePhone(trimmedPhone);
                if (!normalizedPhone) {
                    return failure('Ungültige Telefonnummer', undefined, { field: 'phone' });
                }
                profileContactData.phone = normalizedPhone;
            }
        }

        // Vorfall dk9 (2026-07-28): Ein reines prisma.user.update ließ die
        // Redis-Sitzungsschnappschüsse mit username=null stehen — der Nutzer
        // hing in der /account?setup=1-Schleife, obwohl die DB den Namen
        // längst trug. Identitätsfelder gehen deshalb über den
        // internalAdapter-Wrapper (DB-Write + refreshUserSessions in einem
        // Aufruf); Kontaktfelder kennt better-auth nicht und bleiben bei
        // Prisma.
        //
        // Reihenfolge mit Absicht: Kontaktfelder ZUERST — festgenagelt in
        // __tests__/update-profile-session-refresh.test.ts, weil eine
        // Vertauschung sonst lautlos durchginge (Review 2026-07-29).
        //
        // ACHTUNG, der Halbzustand „DB-Username gesetzt, Sitzung veraltet"
        // ist damit NICHT ausgeschlossen. Der Wrapper schreibt DB und Sitzung
        // zwar im selben Vendor-Aufruf, aber der Sitzungsteil ist fail-soft:
        // Fällt der Redis-Client zwischen dem Wächter und dem ersten
        // `secondaryStorage.get` in den Reconnect-Backoff, liefert `get`
        // `null`, der Vendor kehrt mit `if (!listRaw) return;` ohne Fehler
        // zurück, es kommt zu keinem `set` — und damit bleibt auch die
        // Nachkontrolle blind, weil `noteDegradation` nur an `set`/`delete`
        // hängt. Der Nutzer liest dann „Profil erfolgreich aktualisiert",
        // die DB trägt den Benutzernamen und der Schnappschuss weiter
        // `username: null`. Erneutes Speichern heilt das NICHT: der Wert
        // steht schon in der DB, der Vorgang ist idempotent, und die Sitzung
        // bleibt alt bis Ablauf oder Neuanmeldung. Das ist wörtlich die
        // „RESTLÜCKE, bewusst offen" aus src/lib/auth-user-update.ts.
        //
        // Die zweite Sicherung dagegen — eine Weiterleitung, die nicht am
        // Sitzungsschnappschuss, sondern an der DB entscheidet — steht
        // inzwischen: `src/lib/setup-redirect.ts` (Task 3, verdrahtet in
        // src/app/(dashboard)/layout.tsx und
        // src/components/layout/dashboard-layout.tsx). Der Halbzustand oben
        // bleibt damit zwar möglich, ist aber folgenlos: Er zeigt höchstens
        // einen veralteten Namen im Menü an und sperrt niemanden mehr aus.
        //
        // ZWEI Schreibvorgänge OHNE Transaktion, bewusst — und der harmlose
        // Halbzustand ist damit ausdrücklich in Kauf genommen: Bricht der
        // Identitäts-Write ab, sind Adresse/PLZ/Ort/Telefon bereits
        // gespeichert, obwohl der Nutzer „Fehler beim Aktualisieren des
        // Profils" liest. Das ist die gutartige Richtung — Kontaktdaten
        // steuern keine Navigation, keine Rechte und keine Anmeldung, und der
        // Formularinhalt ist unverändert derselbe, sodass ein zweiter
        // Speicherversuch beide Seiten idempotent nachzieht. Die umgekehrte
        // Reihenfolge wäre die schädliche: dort stünde die Identität in der DB
        // (samt Sitzungsabgleich), während die Kontaktdaten stillschweigend
        // verloren gingen. Eine Transaktion über beide Wege ist NICHT möglich,
        // weil better-auth den Identitäts-Write in eigener Regie ausführt und
        // dabei zusätzlich nach Redis schreibt — ein Rollback der DB würde die
        // bereits erneuerten Sitzungsschnappschüsse ohnehin nicht zurückholen
        // (siehe „KEINE ATOMARITÄT" in src/lib/auth-user-update.ts).
        if (Object.keys(profileContactData).length > 0) {
            await prisma.user.update({
                where: { id: currentUser.id },
                data: profileContactData,
            });
        }

        await updateUserIdentity(currentUser.id, {
            name,
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: nextEmail,
            pendingEmail,
            username,
            emailVerified: nextEmailVerified ?? null,
        });

        // Rückgabewerte aus der DB lesen (der Wrapper liefert das
        // better-auth-transformierte Objekt, nicht die Prisma-Typen).
        const updatedUser = await prisma.user.findUniqueOrThrow({
            where: { id: currentUser.id },
        });

        if (pendingCleared) {
            await prisma.emailVerificationToken.deleteMany({ where: { userId: updatedUser.id } });
        }

        if (verificationTargetEmail) {
            const token = await createEmailVerificationToken(updatedUser.id);
            const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
            const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;
            // Selbst ausgelöst — kein Kontext-Netz, Stufe (b) über die eigenen
            // Bindings (eindeutig → gebrandet, sonst generisch; Entscheidung 6).
            const branding = await resolveMailBranding({ userId: currentUser.id });
            const emailResult = await sendEmail({
                to: verificationTargetEmail,
                subject: 'Bitte E-Mail-Adresse bestaetigen',
                text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
                html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href=\"${verificationUrl}\">${verificationUrl}</a></p>`,
                senderNamePostfix: branding.senderNamePostfix,
                signature: branding.signature,
            });
            if (emailResult.error) {
                // Abschluss-Review 2026-07-29 (M3): Die Vormerkung steht
                // bereits in der Datenbank — sie ist Teil des Identitäts-
                // Writes oben, und die Reihenfolge zu drehen wäre schlimmer
                // (Begründung an EMAIL_VERIFICATION_SEND_FAILED_MESSAGE).
                // Der Kunde bekommt deshalb keine rohe SMTP-Meldung, sondern
                // die Beschreibung des Zustands, in dem sein Konto wirklich
                // ist, samt Weg heraus. Der Klartext bleibt beim Betreiber.
                logger.warning(
                    'Bestätigungsmail konnte nicht versendet werden',
                    {
                        userId: currentUser.id,
                        fehler: emailResult.error,
                        pfad: 'updateProfile',
                    },
                    'EmailChange',
                );
                // Nachprüfung 2026-07-29 (N1): Die Meldung verweist auf den
                // Knopf „Bestätigungslink erneut senden" — der wird aber nur
                // gerendert, wenn die Oberfläche den offenen Wechsel KENNT.
                // Der Fehlerzweig des Formulars erfuhr davon nichts, also war
                // das Versprechen in derselben Seitensitzung uneinlösbar; nur
                // ein manueller Vollreload half. Ein Versprechen, das die
                // Oberfläche nicht einlöst, ist schlechter als keins.
                //
                // Zwei Dinge werden deshalb nachgezogen, und beide beschreiben
                // nur, was ohnehin schon passiert IST:
                //  1. `revalidatePath` — die Zeile ist geschrieben, der
                //     Server-Cache muss das wissen. Ohne das zeigte eine
                //     spätere Soft-Navigation auf /account weiter den Stand
                //     von vor dem Speichern.
                //  2. Der gespeicherte Kontostand in `details`, damit das
                //     Formular Feld und Hinweise nachziehen kann, statt eine
                //     veraltete Sicht stehen zu lassen.
                //
                // NUR der eigene Kontostand, nichts sonst: `details` ist
                // derselbe Kanal, über den in dieser Aufgabe beinahe ein
                // Gate-Ausgang geleakt wäre.
                revalidatePath('/account');
                return failure(EMAIL_VERIFICATION_SEND_FAILED_MESSAGE, undefined, {
                    savedState: {
                        email: updatedUser.email,
                        pendingEmail: updatedUser.pendingEmail,
                        emailVerified: updatedUser.emailVerified,
                    },
                });
            }
        }

        revalidatePath('/account');
        // `email` gehört ins Ergebnis (Abschluss-Review 2026-07-29, M4): Die
        // Kontoansicht muss ihr Eingabefeld nach dem Speichern auf die
        // weiterhin GÜLTIGE Adresse zurücksetzen können. Ohne sie behielte das
        // Feld die beantragte Adresse, und der dokumentierte Rückweg („einmal
        // erneut speichern verwirft den Wechsel", EMAIL_CHANGE_PENDING_-
        // BLOCKED_MESSAGE) funktionierte in derselben Seitensitzung nicht:
        // Eingabe gleich `pendingEmail` heisst „Antrag steht weiter", nicht
        // „Antrag verwerfen". Erst ein Vollreload stellte den Rückweg her.
        return ok({
            emailVerified: updatedUser.emailVerified,
            pendingEmail: updatedUser.pendingEmail,
            email: updatedUser.email,
        });
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User profile update failed', e);
        return failure('Fehler beim Aktualisieren des Profils');
    }
}

export async function registerUser(data: {
    firstName: string;
    lastName: string;
    email: string;
    username?: string;
    password: string;
    recaptchaToken: string;
    inviteToken?: string;
}): Promise<ActionResult> {
    try {
        // Code-review S-A (2026-05-13): per-IP rate limit on top of
        // reCAPTCHA — reCAPTCHA can be solved at scale by paid services
        // and doesn't bound the per-host request rate.
        const ip = await getRequestIp();
        const { success: ipOk } = await registerIpLimiter.limit(`register-ip:${ip}`);
        if (!ipOk) {
            // Same generic German message as the duplicate-email path
            // below — don't expose whether the rate-limit was hit.
            return failure('Registrierung konnte nicht abgeschlossen werden. Bitte versuchen Sie es später erneut.');
        }

        const email = data.email.trim().toLowerCase();
        const name = buildDisplayName(data.firstName, data.lastName);
        const baseUsername = buildUsernameBase(data.firstName, data.lastName);
        const usernameInput = data.username?.trim() ?? '';
        const username = usernameInput || await findAvailableUsername(baseUsername);

        if (!email || !username || !data.password || !name) {
            return failure('Bitte alle Pflichtfelder ausfüllen');
        }

        if (data.password.length < MIN_PASSWORD_LENGTH) {
            return failure(`Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
        }

        const recaptcha = await verifyRecaptcha(data.recaptchaToken);
        if (!recaptcha.success) {
            return failure(recaptcha.error || 'reCAPTCHA fehlgeschlagen');
        }

        if (!isSmtpConfigured()) {
            const missing = getMissingSmtpConfig();
            const missingHint = missing.length > 0 ? ` (fehlt: ${missing.join(', ')})` : '';
            return failure(`SMTP ist nicht konfiguriert${missingHint}`);
        }

        // Code-review S-A (2026-05-13): merge "email exists" and
        // "username taken" responses into a single generic message so
        // the action can't be used as a registered-email enumeration
        // oracle. The legitimate user knows they have an account; the
        // attacker can't tell email-taken from username-taken from
        // "you fat-fingered your firstName".
        const GENERIC_REGISTER_BLOCKED =
            'Diese Daten können nicht registriert werden. Falls Sie schon ein Konto haben, melden Sie sich an oder setzen Sie Ihr Passwort zurück.';

        const existingEmail = await prisma.user.findUnique({ where: { email } });
        if (existingEmail) {
            // Check if this is a placeholder user (invited but never registered)
            if (existingEmail.invitedAt && !existingEmail.password) {
                // Upgrade placeholder to real user — keep existing AccessBindings
            } else {
                return failure(GENERIC_REGISTER_BLOCKED);
            }
        }

        const existingUsername = await prisma.user.findUnique({ where: { username } });
        if (existingUsername && existingUsername.id !== existingEmail?.id) {
            return failure(GENERIC_REGISTER_BLOCKED);
        }

        // Kundenidentität Paket 1, Riegel an der WURZEL (Review-Nachfassung
        // 2026-07-27).
        //
        // Er steht VOR der Verzweigung, nicht in einem ihrer Zweige. Unten
        // gibt es drei Wege, und zwei davon übergeben eine BESTEHENDE
        // Portal-User-ID an den Anmelder: die Reaktivierung des gelöschten
        // Kontos und die Aufwertung eines Platzhalters. Der Platzhalter-Zweig
        // gewinnt dabei — genau daran lief eine frühere, im Reaktivierungszweig
        // sitzende Prüfung vorbei: Konto A mit Supporthistorie wird gelöscht,
        // eine Anlageneinladung legt auf dieselbe, wieder freie Adresse einen
        // Platzhalter B an (`plant-permissions.ts`), und die Selbstregistrierung
        // wertete B auf, ohne den Riegel je auszuführen.
        //
        // Hängt an der Adresse eine Supportidentität, erbt der Anmelder sie:
        // Die Ticketautorisierung läuft in Paket 1 bewusst weiter über die
        // E-Mail-Adresse, also sähe er die Historie seines Vorgängers, könnte
        // antworten und Anhänge laden. Der Grabstein weiter unten verhindert
        // nur die WIEDERBINDUNG — gelesen wird er erst in Paket 2.
        //
        // Antwort ist bewusst dieselbe wie bei einer belegten Adresse: Ein
        // eigener Text verriete, dass hinter dieser Adresse einmal ein Konto
        // mit Supporthistorie stand — eine Auskunft über fremde Konten an
        // jeden, der ein Registrierungsformular ausfüllt. Den Grund erfährt der
        // Betreiber aus dem Protokoll; von Hand ist der Fall lösbar
        // (Wiederherstellen oder Ablösen der Bindung, siehe
        // docs/operations/zammad-email-wechsel-manuell.md).
        const emailReuse = await isEmailReuseBlocked(email);
        if (emailReuse.blocked) {
            void safeLogSystemError(
                'Registrierung abgelehnt: Supportidentität an der Adresse eines gelöschten Kontos',
                undefined,
                { reason: emailReuse.reason },
            );
            return failure(GENERIC_REGISTER_BLOCKED);
        }

        const hashedPassword = await bcrypt.hash(data.password, SALT_ROUNDS);

        // Check for soft-deleted user with same email → reactivate
        //
        // `deletedEmail` ist nicht eindeutig (nur indiziert), es kann also
        // mehrere Vorgänger geben. Welchen dieser Zweig reaktiviert, ist
        // fachlich beliebig — der Riegel oben hat für ALLE bereits
        // festgestellt, dass an dieser Adresse keine Supportidentität hängt.
        // `orderBy` macht die Wahl wenigstens reproduzierbar.
        const deletedUser = await prisma.user.findFirst({
            where: { deletedEmail: email, deletedAt: { not: null } },
            orderBy: { id: 'asc' },
        });

        let userId: number;

        if (existingEmail?.invitedAt && !existingEmail.password) {
            // Upgrade placeholder user to real account (keep existing AccessBindings)
            await prisma.user.update({
                where: { id: existingEmail.id },
                data: {
                    name,
                    firstName: data.firstName.trim(),
                    lastName: data.lastName.trim(),
                    username,
                    password: hashedPassword,
                    invitedAt: null,
                    emailVerified: null,
                    creationSource: 'self_registration',
                },
            });

            await logAuditEvent({
                userId: existingEmail.id,
                action: 'user.upgrade_placeholder',
                resourceType: 'User',
                resourceId: existingEmail.id,
                metadata: {
                    method: 'self_registration',
                    previousCreationSource: existingEmail.creationSource,
                },
            });

            userId = existingEmail.id;
        } else if (deletedUser) {
            // Der Riegel gegen das Erben einer fremden Supportidentität steht
            // oben, vor der Verzweigung — er gilt für diesen Zweig UND für die
            // Platzhalter-Aufwertung. Hier ist bereits entschieden, dass an
            // dieser Adresse keine Supportidentität hängt.
            //
            // Reactivate: restore identity, wipe permissions, set new credentials
            await prisma.$transaction([
                prisma.user.update({
                    where: { id: deletedUser.id },
                    data: {
                        deletedAt: null,
                        deletedById: null,
                        email,
                        username,
                        deletedEmail: null,
                        deletedUsername: null,
                        password: hashedPassword,
                        name,
                        firstName: data.firstName.trim(),
                        lastName: data.lastName.trim(),
                        emailVerified: null,
                        creationSource: data.inviteToken ? 'invite' : 'self_registration',
                    },
                }),
                prisma.accessBinding.deleteMany({ where: { userId: deletedUser.id } }),
                prisma.groupMembership.deleteMany({ where: { userId: deletedUser.id } }),
                prisma.report.deleteMany({ where: { userId: deletedUser.id } }),
                // Kundenidentität Paket 1: Die Reaktivierung vergibt dieselbe
                // Portal-User-ID an denjenigen, der die Adresse jetzt
                // besitzt — das muss nicht derselbe Mensch sein. Die alte
                // Bindung wird deshalb zum Grabstein (detached), nicht
                // gelöscht und nicht bloss genullt: Nur der Grabstein hält
                // die automatische Wiederbindung auf, die sonst beim
                // nächsten Ticket über die E-Mail-Suche zustande käme und
                // dem neuen Inhaber die fremde Supporthistorie lieferte.
                detachZammadBinding(
                    prisma,
                    deletedUser.id,
                    `user.reactivate: Selbstregistrierung nach Soft-Delete (vorher ${deletedUser.deletedEmail})`,
                ),
            ]);

            await logAuditEvent({
                userId: deletedUser.id,
                action: 'user.reactivate',
                resourceType: 'User',
                resourceId: deletedUser.id,
                metadata: {
                    method: 'self_registration',
                    previousEmail: deletedUser.deletedEmail,
                },
            });

            userId = deletedUser.id;
        } else {
            const newUser = await prisma.user.create({
                data: {
                    name,
                    firstName: data.firstName.trim(),
                    lastName: data.lastName.trim(),
                    email,
                    username,
                    password: hashedPassword,
                    emailVerified: null,
                    creationSource: data.inviteToken ? 'invite' : 'self_registration',
                }
            });
            userId = newUser.id;
        }

        // Review A7 (2026-07-25): Better-Auth meldet ausschliesslich über die
        // Account-Zeile mit providerId='credential' an — `User.password`
        // allein reicht NICHT. Ohne diese Zeile konnte sich niemand anmelden,
        // der sich selbst registriert hat (auch nicht nach der
        // E-Mail-Bestätigung). Gilt für alle drei Zweige oben
        // (Placeholder-Upgrade, Reaktivierung, Neuanlage) — deshalb hier
        // einmal zentral nach der Verzweigung, idempotent.
        //
        // Reihenfolge-Hinweis: Anmeldbar wird das Konto trotzdem erst nach der
        // E-Mail-Bestätigung — requireEmailVerification (A1, auth.ts) ist die
        // Gegenseite dieses Fixes und MUSS zusammen mit ihm ausgeliefert
        // werden, sonst wird aus der Selbstregistrierung eine
        // Selbstbedienungs-Lücke.
        await upsertCredentialAccount(userId, hashedPassword);

        // Spec #5: Netz der Einladung für das Mail-Branding aus dem Block heben.
        let inviteNetworkId: number | null = null;

        // Consume invite token if present
        if (data.inviteToken) {
            try {
                const invite = await prisma.inviteToken.findFirst({
                    where: {
                        // F6: Lookup per SHA-256-Hash des eingehenden Roh-Tokens.
                        token: hashToken(data.inviteToken),
                        email,
                        consumedAt: null,
                        expiresAt: { gt: new Date() },
                    },
                });
                if (invite) {
                    inviteNetworkId = invite.networkId;
                    await prisma.inviteToken.update({
                        where: { id: invite.id },
                        data: { consumedAt: new Date() },
                    });

                    await prisma.user.update({
                        where: { id: userId },
                        data: {
                            createdById: invite.invitedById,
                        },
                    });

                    let parsedPendingRoles: PendingRoleInput[] = [];
                    try {
                        const rawPending = JSON.parse(invite.pendingRoles || '[]');
                        if (Array.isArray(rawPending)) {
                            parsedPendingRoles = rawPending
                                .filter((item): item is { roleSlug: string; networkId: number; plantMemberNr?: string } => {
                                    return typeof item?.roleSlug === 'string'
                                        && Number.isFinite(Number(item?.networkId));
                                })
                                .map((item) => ({
                                    roleSlug: item.roleSlug,
                                    networkId: Number(item.networkId),
                                    plantMemberNr: typeof item.plantMemberNr === 'string' ? item.plantMemberNr : undefined,
                                }));
                        }
                    } catch {
                        parsedPendingRoles = [];
                    }

                    const validatedPendingRoles = await validatePendingRoleAssignments({
                        actorUserId: invite.invitedById,
                        pendingRoles: parsedPendingRoles,
                        enforcedNetworkId: invite.networkId ?? undefined,
                        strict: false,
                    });
                    if (validatedPendingRoles.rejected.length > 0) {
                        logger.warning('Skipping invalid invite pending roles', { rejected: validatedPendingRoles.rejected }, 'Register');
                    }

                    // Review A3 (2026-07-25), Invite-Teil: Die aus einer
                    // Einladung materialisierten Bindings waren IMMER
                    // unbefristet — ein befristeter Netz-Admin konnte über den
                    // Umweg Einladung dauerhaften Zugriff vergeben, obwohl
                    // addAccessBinding/grantPlantAccess seit C3 klemmen.
                    // Das Fenster des Einladenden wird zum Einlösezeitpunkt
                    // aufgelöst (nicht zum Einladungszeitpunkt) — dieselbe
                    // Aktualität, die validatePendingRoleAssignments oben für
                    // die Berechtigung selbst nutzt.
                    const pendingRoleMeta = await prisma.role.findMany({
                        where: { id: { in: validatedPendingRoles.roles.map((r) => r.roleId) } },
                        select: { id: true, assignableBy: true },
                    });
                    const assignableByRoleId = new Map(
                        pendingRoleMeta.map((role) => [role.id, role.assignableBy]),
                    );

                    for (const pending of validatedPendingRoles.roles) {
                        const grantorWindow = await resolveGrantorWindow({
                            grantorUserId: invite.invitedById,
                            assignableBy: assignableByRoleId.get(pending.roleId),
                            networkId: pending.networkId,
                            plantMemberNr: pending.plantMemberNr,
                        });
                        const { validFrom, validUntil } = clampWindowToAdminWindow(
                            { validFrom: null, validUntil: null },
                            grantorWindow,
                        );
                        await prisma.accessBinding.create({
                            data: {
                                userId,
                                roleId: pending.roleId,
                                networkId: pending.networkId,
                                plantMemberNr: pending.plantMemberNr,
                                createdById: invite.invitedById,
                                validFrom,
                                validUntil,
                                notes: 'Auto-assigned from invitation',
                            },
                        });
                    }
                }
            } catch (e) {
                console.error('[Register] Failed to consume invite token:', e);
            }
        }

        const token = await createEmailVerificationToken(userId);
        const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
        const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;

        // Invite-Flow: Netz der Einladung gewinnt; ohne Invite bzw. bei
        // invite.networkId == null greift Stufe (b) über die soeben aus
        // pendingRoles materialisierten Bindings.
        const branding = await resolveMailBranding({ networkId: inviteNetworkId, userId });
        const emailResult = await sendEmail({
            to: email,
            subject: 'Bitte E-Mail-Adresse bestaetigen',
            text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
            html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href=\"${verificationUrl}\">${verificationUrl}</a></p>`,
            senderNamePostfix: branding.senderNamePostfix,
            signature: branding.signature,
        });
        if (emailResult.error) {
            return failure(emailResult.error);
        }

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User register failed', e, { email: data.email, username: data.username });
        return failure('Fehler bei der Registrierung');
    }
}

export async function disconnectAccount(accountId: number): Promise<ActionResult> {
    try {
        const userId = await getCurrentUserId();
        if (!userId) return failure('Nicht eingeloggt');

        // Verify account belongs to user
        const account = await prisma.account.findUnique({ where: { id: accountId } });
        if (!account || account.userId !== userId) {
            return failure('Account nicht gefunden');
        }

        // Check safety: Prevent deletion of last login method
        const user = await prisma.user.findUnique({
            where: { id: userId },
            include: { accounts: true }
        });

        if (!user) return failure('User nicht gefunden');

        const hasPassword = !!user.password;
        const accountCount = user.accounts.length;

        if (!hasPassword && accountCount <= 1) {
            return failure('Sie können nicht Ihre letzte Login-Methode entfernen. Bitte setzen Sie zuerst ein Passwort.');
        }

        await prisma.account.delete({ where: { id: accountId } });
        revalidatePath('/account');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('Account disconnect failed', e, { accountId });
        return failure('Fehler beim Trennen der Verbindung');
    }
}
